/**
 * Estimate detail + builder screen (WO-M4 + estimate-builder track).
 *
 * Two modes, decided by approval_status:
 *   - EDITABLE (draft | presented): line items get edit + delete; an "Add line item"
 *     form appends; a "Discount" control sets/clears the in-app discount. Every
 *     mutation returns the recomputed EstimateDetail → totals refresh live (the
 *     query cache is updated + invalidated).
 *   - READ-ONLY (approved | sent_for_signature | signed | declined | expired):
 *     the original read view.
 *
 * Role gate (CLAUDE.md invariant 6, also enforced server-side):
 *   staff  → may build (add/edit/delete lines, discount) + Present.
 *   owner  → all of the above + Approve, Send for Signature, Convert to Job, Create Invoice.
 * Owner-only binding actions are NOT rendered for staff.
 *
 * estimateId is an opaque UUID — no PII in URLs (CLAUDE.md §2).
 * NEVER imports @smartbox/* (mobile CLAUDE.md invariant 1). No raw style values.
 */

import { useState } from 'react';
import { ActivityIndicator, StyleSheet, View } from 'react-native';
import { useLocalSearchParams, useRouter } from 'expo-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getEstimate, transitionEstimate } from '../../../src/api/billing';
import {
  addLineItem,
  updateLineItem,
  deleteLineItem,
  setEstimateDiscount,
  convertEstimateToJob,
  createInvoiceFromEstimate,
} from '../../../src/api/estimates';
import { useSession } from '../../../src/auth/session';
import { OwnerGatedControl } from '../../../src/components/help';
import { createTask } from '../../../src/api/tasks';
import { LineItemForm } from '../../../src/components/estimates/LineItemForm';
import {
  Screen,
  Text,
  Badge,
  Card,
  Button,
  Field,
  Select,
  estimateBadge,
} from '../../../src/components/ui';
import { theme } from '../../../src/theme';
import { formatCurrency, formatQuantity, extractErrorMessage, humanize } from '../../../src/lib/format';
import type {
  EstimateDetail,
  EstimateLineItemDto,
  ApprovalStatus,
  AddLineItemRequest,
  UpdateLineItemRequest,
  LineType,
} from '@beam/api-types';

const DISCOUNT_TYPE_OPTIONS: ReadonlyArray<{ value: 'amount' | 'percent'; label: string }> = [
  { value: 'percent', label: 'Percent %' },
  { value: 'amount', label: 'Flat amount' },
];

// Approval statuses where the builder is editable.
const EDITABLE_STATUSES = new Set(['draft', 'presented']);

// ---------------------------------------------------------------------------
// Discount control
// ---------------------------------------------------------------------------

function DiscountForm({
  current,
  busy,
  onApply,
  onClear,
  onCancel,
}: {
  current: EstimateDetail;
  busy: boolean;
  onApply: (type: 'amount' | 'percent', value: number) => void;
  onClear: () => void;
  onCancel: () => void;
}) {
  const [type, setType] = useState<'amount' | 'percent'>('percent');
  const [value, setValue] = useState('');
  const [err, setErr] = useState<string | null>(null);

  const apply = () => {
    setErr(null);
    const num = parseFloat(value);
    if (Number.isNaN(num) || num < 0) {
      setErr('Enter a discount value of 0 or more.');
      return;
    }
    onApply(type, num);
  };

  const hasDiscount = !!current.discountTotal && current.discountTotal !== '0.00';

  return (
    <Card>
      <Text variant="bodyStrong">Discount</Text>
      <Select label="Type" options={DISCOUNT_TYPE_OPTIONS} value={type} onChange={setType} />
      <Field
        label={type === 'percent' ? 'Percent off' : 'Amount off'}
        placeholder={type === 'percent' ? 'e.g. 10' : 'e.g. 150.00'}
        value={value}
        onChangeText={setValue}
        keyboardType="decimal-pad"
        accessibilityLabel="Discount value"
      />
      {err ? (
        <Text variant="small" color="danger" accessibilityRole="alert">
          {err}
        </Text>
      ) : null}
      <View style={styles.formActions}>
        <Button title="Apply discount" onPress={apply} loading={busy} disabled={busy} />
        {hasDiscount ? (
          <Button title="Remove discount" variant="danger" onPress={onClear} disabled={busy} />
        ) : null}
        <Button title="Cancel" variant="secondary" onPress={onCancel} disabled={busy} />
      </View>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Line-item row (read or with edit/delete affordances)
// ---------------------------------------------------------------------------

function LineItemRow({
  item,
  currency,
  editable,
  busy,
  onEdit,
  onDelete,
}: {
  item: EstimateLineItemDto;
  currency: string;
  editable: boolean;
  busy: boolean;
  onEdit: () => void;
  onDelete: () => void;
}) {
  return (
    <View style={styles.lineRow}>
      <View style={styles.lineDesc}>
        {item.section ? (
          <Text variant="label" color="secondary">
            {item.section}
          </Text>
        ) : null}
        <Text variant="bodyStrong">{item.description}</Text>
        <Text variant="small" color="secondary">
          {item.lineType === 'labor'
            ? `Labor · ${formatQuantity(item.quantity)} hr × ${formatCurrency(item.unitPrice ?? item.unitCost, currency)}/hr`
            : `${humanize(item.lineType)} · qty ${formatQuantity(item.quantity)} × ${formatCurrency(item.unitPrice ?? item.unitCost, currency)}`}
          {item.isOptional ? ' · optional' : ''}
        </Text>
        {editable ? (
          <View style={styles.lineActions}>
            <Button
              title="Edit"
              variant="ghost"
              size="sm"
              fullWidth={false}
              onPress={onEdit}
              disabled={busy}
              accessibilityLabel={`Edit ${item.description}`}
            />
            <Button
              title="Delete"
              variant="danger"
              size="sm"
              fullWidth={false}
              onPress={onDelete}
              disabled={busy}
              accessibilityLabel={`Delete ${item.description}`}
            />
          </View>
        ) : null}
      </View>
      <Text variant="data" style={styles.lineTotal}>
        {item.lineTotal ? formatCurrency(item.lineTotal, currency) : '—'}
      </Text>
    </View>
  );
}

// ---------------------------------------------------------------------------
// Screen
// ---------------------------------------------------------------------------

export default function EstimateDetailScreen() {
  const router = useRouter();
  const { id } = useLocalSearchParams<{ id: string }>();
  const sessionUser = useSession((s) => s.user);
  const queryClient = useQueryClient();

  const [actionError, setActionError] = useState<string | null>(null);
  const [approvalRequested, setApprovalRequested] = useState(false);
  const requestApprovalMutation = useMutation({
    mutationFn: () =>
      createTask({
        title: 'Owner approval requested for estimate',
        task_type: 'estimate_followup',
        estimate_id: id,
      }),
    onSuccess: () => setApprovalRequested(true),
  });
  const [adding, setAdding] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [showDiscount, setShowDiscount] = useState(false);

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['estimates', id],
    queryFn: () => getEstimate(id!),
    enabled: !!id,
    staleTime: 30_000,
  });

  // Shared cache-update for any mutation returning the recomputed EstimateDetail.
  const applyUpdate = (updated: EstimateDetail) => {
    queryClient.setQueryData(['estimates', id], updated);
    void queryClient.invalidateQueries({ queryKey: ['estimates'] });
    setActionError(null);
  };
  const onMutError = (err: unknown) => setActionError(extractErrorMessage(err));

  const transitionMutation = useMutation({
    mutationFn: (to: ApprovalStatus) => transitionEstimate(id!, to),
    onSuccess: applyUpdate,
    onError: onMutError,
  });
  const addMutation = useMutation({
    mutationFn: (body: AddLineItemRequest) => addLineItem(id!, body),
    onSuccess: (updated) => {
      applyUpdate(updated);
      setAdding(false);
    },
    onError: onMutError,
  });
  const updateMutation = useMutation({
    mutationFn: ({ lineId, body }: { lineId: string; body: UpdateLineItemRequest }) =>
      updateLineItem(id!, lineId, body),
    onSuccess: (updated) => {
      applyUpdate(updated);
      setEditingId(null);
    },
    onError: onMutError,
  });
  const deleteMutation = useMutation({
    mutationFn: (lineId: string) => deleteLineItem(id!, lineId),
    onSuccess: applyUpdate,
    onError: onMutError,
  });
  const discountMutation = useMutation({
    mutationFn: (discount: { type: 'amount' | 'percent'; value: number } | null) =>
      setEstimateDiscount(id!, { discount }),
    onSuccess: (updated) => {
      applyUpdate(updated);
      setShowDiscount(false);
    },
    onError: onMutError,
  });
  const convertMutation = useMutation({
    mutationFn: () => convertEstimateToJob(id!),
    onSuccess: ({ jobId }) => {
      void queryClient.invalidateQueries({ queryKey: ['estimates'] });
      void queryClient.invalidateQueries({ queryKey: ['jobs'] });
      setActionError(null);
      // Navigate to the new job so the action has a visible result (it previously refetched in place,
      // so the screen looked unchanged — "nothing happened"). Conversion is idempotent server-side.
      if (jobId) router.push(`/(app)/jobs/${jobId}`);
      else void refetch();
    },
    onError: onMutError,
  });
  const invoiceMutation = useMutation({
    mutationFn: () => createInvoiceFromEstimate(id!),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['invoices'] });
      setActionError(null);
      router.push('/(app)/invoices');
    },
    onError: onMutError,
  });

  const role = sessionUser?.role ?? 'staff';
  const isOwner = role === 'owner';
  const isStaff = role === 'staff';

  const approvalStatus = data?.approvalStatus ?? '';
  const editable = EDITABLE_STATUSES.has(approvalStatus);

  const anyBusy =
    addMutation.isPending ||
    updateMutation.isPending ||
    deleteMutation.isPending ||
    discountMutation.isPending;

  const canPresent =
    (isStaff || isOwner) && approvalStatus === 'draft' && !transitionMutation.isPending;
  const canApprove =
    isOwner && approvalStatus === 'presented' && !transitionMutation.isPending;
  const canSendForSignature =
    isOwner && approvalStatus === 'approved' && !transitionMutation.isPending;
  const canConvertToJob =
    isOwner && (approvalStatus === 'approved' || approvalStatus === 'signed');
  const canCreateInvoice =
    isOwner && (approvalStatus === 'approved' || approvalStatus === 'signed');

  if (!id) {
    return (
      <Screen>
        <View style={styles.centeredState}>
          <Text variant="body" color="danger">
            No estimate ID provided.
          </Text>
        </View>
      </Screen>
    );
  }

  if (isLoading) {
    return (
      <Screen>
        <View style={styles.centeredState}>
          <ActivityIndicator
            size="large"
            color={theme.color.action.primary}
            accessibilityLabel="Loading estimate"
          />
          <Text variant="body" color="secondary">
            Loading…
          </Text>
        </View>
      </Screen>
    );
  }

  if (isError) {
    return (
      <Screen>
        <View style={styles.centeredState}>
          <Card>
            <Text variant="bodyStrong" color="danger" accessibilityRole="alert">
              {extractErrorMessage(error)}
            </Text>
            <Button
              title="Retry"
              variant="secondary"
              size="sm"
              fullWidth={false}
              onPress={() => refetch()}
              accessibilityLabel="Retry loading estimate"
            />
          </Card>
        </View>
      </Screen>
    );
  }

  if (!data) return null;

  const b = estimateBadge(data.approvalStatus ?? data.status);
  const sortedLines = data.lineItems.slice().sort((a, c) => a.sortOrder - c.sortOrder);

  return (
    <Screen scroll>
      <View style={styles.statusRow}>
        <Text variant="title">Estimate</Text>
        <Badge status={b.key} label={b.label} />
      </View>

      {/* Summary */}
      <Card>
        <View style={styles.cardRow}>
          <Text variant="small" color="secondary">
            Estimate ID
          </Text>
          <Text variant="data">{data.id.slice(0, 8).toUpperCase()}</Text>
        </View>
        <View style={styles.cardRow}>
          <Text variant="small" color="secondary">
            Status
          </Text>
          <Text variant="data">{humanize(data.status)}</Text>
        </View>
        <View style={styles.cardRow}>
          <Text variant="small" color="secondary">
            Currency
          </Text>
          <Text variant="data">{data.currency}</Text>
        </View>
        {data.validUntil ? (
          <View style={styles.cardRow}>
            <Text variant="small" color="secondary">
              Valid Until
            </Text>
            <Text variant="data">{new Date(data.validUntil).toLocaleDateString()}</Text>
          </View>
        ) : null}
      </Card>

      {/* Line items */}
      <View style={styles.section}>
        <Text variant="label" color="secondary">
          Line Items
        </Text>
        {sortedLines.length > 0 ? (
          <Card style={styles.lineItemsCard}>
            {sortedLines.map((item) =>
              editingId === item.id ? (
                <View key={item.id} style={styles.editWrap}>
                  <LineItemForm
                    initial={item}
                    busy={updateMutation.isPending}
                    onCancel={() => setEditingId(null)}
                    onSubmit={(v) =>
                      updateMutation.mutate({
                        lineId: item.id,
                        body: {
                          description: v.description,
                          quantity: v.quantity,
                          unitPrice: v.unitPrice,
                          section: v.section,
                          isOptional: v.isOptional,
                        },
                      })
                    }
                  />
                </View>
              ) : (
                <LineItemRow
                  key={item.id}
                  item={item}
                  currency={data.currency}
                  editable={editable && editingId === null && !adding}
                  busy={anyBusy}
                  onEdit={() => setEditingId(item.id)}
                  onDelete={() => deleteMutation.mutate(item.id)}
                />
              ),
            )}
          </Card>
        ) : (
          <Card>
            <Text variant="body" color="secondary">
              No line items yet.
            </Text>
          </Card>
        )}

        {/* Add lines — shortcut-and-edit first (WO-D5), manual custom line as ONE option. */}
        {editable && editingId === null ? (
          adding ? (
            <LineItemForm
              busy={addMutation.isPending}
              onCancel={() => setAdding(false)}
              onSubmit={(v) =>
                addMutation.mutate({
                  lineType: v.lineType,
                  description: v.description,
                  quantity: v.quantity,
                  unitPrice: v.unitPrice,
                  section: v.section,
                  isOptional: v.isOptional,
                })
              }
            />
          ) : (
            <View style={styles.addActions}>
              <Button
                title="+ Add service"
                variant="primary"
                onPress={() =>
                  router.push({ pathname: '/(app)/estimates/add-service', params: { estimateId: id } })
                }
                accessibilityLabel="Add a service (prefills materials and labor)"
              />
              <Button
                title="+ Add material"
                variant="secondary"
                onPress={() =>
                  router.push({ pathname: '/(app)/estimates/add-material', params: { estimateId: id } })
                }
                accessibilityLabel="Add a material from your most-used"
              />
              <Button
                title="+ Custom line"
                variant="ghost"
                onPress={() => setAdding(true)}
                accessibilityLabel="Add a custom line item manually"
              />
            </View>
          )
        ) : null}
      </View>

      {/* Totals */}
      <Card>
        {data.subtotal ? (
          <View style={styles.cardRow}>
            <Text variant="small" color="secondary">
              Subtotal
            </Text>
            <Text variant="data">{formatCurrency(data.subtotal, data.currency)}</Text>
          </View>
        ) : null}
        {data.discountTotal && data.discountTotal !== '0.00' ? (
          <View style={styles.cardRow}>
            <Text variant="small" color="secondary">
              Discount
            </Text>
            <Text variant="data" color="link">
              −{formatCurrency(data.discountTotal, data.currency)}
            </Text>
          </View>
        ) : null}
        {data.taxAmount && data.taxAmount !== '0.00' ? (
          <View style={styles.cardRow}>
            <Text variant="small" color="secondary">
              Tax
            </Text>
            <Text variant="data">{formatCurrency(data.taxAmount, data.currency)}</Text>
          </View>
        ) : null}
        <View style={[styles.cardRow, styles.grandTotalRow]}>
          <Text variant="bodyStrong">Total</Text>
          <Text variant="amount">{formatCurrency(data.total, data.currency)}</Text>
        </View>
      </Card>

      {/* Discount control */}
      {editable && editingId === null && !adding ? (
        showDiscount ? (
          <DiscountForm
            current={data}
            busy={discountMutation.isPending}
            onApply={(type, value) => discountMutation.mutate({ type, value })}
            onClear={() => discountMutation.mutate(null)}
            onCancel={() => setShowDiscount(false)}
          />
        ) : (
          <Button
            title="Discount"
            variant="secondary"
            onPress={() => setShowDiscount(true)}
            accessibilityLabel="Set or change discount"
          />
        )
      ) : null}

      {actionError ? (
        <Card>
          <Text variant="bodyStrong" color="danger" accessibilityRole="alert">
            {actionError}
          </Text>
        </Card>
      ) : null}

      {/* Approval + lifecycle actions */}
      <View style={styles.actionsSection}>
        {canPresent ? (
          <Button
            title="Present to Customer"
            variant="primary"
            loading={transitionMutation.isPending}
            disabled={transitionMutation.isPending}
            onPress={() => transitionMutation.mutate('presented')}
            accessibilityLabel="Present estimate to customer"
          />
        ) : null}
        {canApprove ? (
          <Button
            title="Approve"
            variant="accent"
            loading={transitionMutation.isPending}
            disabled={transitionMutation.isPending}
            onPress={() => transitionMutation.mutate('approved')}
            accessibilityLabel="Approve estimate (owner only)"
          />
        ) : null}
        {isStaff && approvalStatus === 'presented' ? (
          <OwnerGatedControl
            reason="Only the owner can approve this estimate and set binding pricing."
            requestLabel={
              approvalRequested
                ? 'Approval requested ✓'
                : requestApprovalMutation.isPending
                  ? 'Sending request…'
                  : 'Ask the owner to approve'
            }
            onRequest={approvalRequested ? undefined : () => requestApprovalMutation.mutate()}
          >
            <Button
              title="Approve"
              variant="accent"
              disabled
              onPress={() => undefined}
              accessibilityLabel="Approve estimate (owner only)"
            />
          </OwnerGatedControl>
        ) : null}
        {canSendForSignature ? (
          <Button
            title="Send for Signature"
            variant="accent"
            loading={transitionMutation.isPending}
            disabled={transitionMutation.isPending}
            onPress={() => transitionMutation.mutate('sent_for_signature')}
            accessibilityLabel="Send estimate for customer signature (owner only)"
          />
        ) : null}
        {canConvertToJob ? (
          <Button
            title="Convert to Job"
            variant="primary"
            loading={convertMutation.isPending}
            disabled={convertMutation.isPending}
            onPress={() => convertMutation.mutate()}
            accessibilityLabel="Convert estimate to a job (owner only)"
          />
        ) : null}
        {canCreateInvoice ? (
          <Button
            title="Create Invoice"
            variant="secondary"
            loading={invoiceMutation.isPending}
            disabled={invoiceMutation.isPending}
            onPress={() => invoiceMutation.mutate()}
            accessibilityLabel="Create an invoice from this estimate (owner only)"
          />
        ) : null}
      </View>

      {isStaff ? (
        <Card>
          <Text variant="small" color="secondary" style={styles.centeredText}>
            You can build and present this estimate. Pricing approval, send-for-signature,
            convert-to-job and invoicing require an owner.
          </Text>
        </Card>
      ) : null}
    </Screen>
  );
}

const styles = StyleSheet.create({
  centeredState: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    gap: theme.spacing.md,
    paddingHorizontal: theme.spacing['2xl'],
  },
  statusRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  cardRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  grandTotalRow: {
    paddingTop: theme.spacing.md,
    borderTopWidth: theme.layout.hairline,
    borderTopColor: theme.color.border.default,
    marginTop: theme.spacing.xs,
  },
  section: {
    gap: theme.spacing.sm,
  },
  lineItemsCard: {
    gap: 0,
    padding: 0,
    overflow: 'hidden',
  },
  lineRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    paddingHorizontal: theme.spacing.lg,
    paddingVertical: theme.spacing.md,
    borderBottomWidth: theme.layout.hairline,
    borderBottomColor: theme.color.border.default,
    gap: theme.spacing.sm,
  },
  lineDesc: {
    flex: 1,
    gap: theme.spacing.xs,
  },
  lineActions: {
    flexDirection: 'row',
    gap: theme.spacing.sm,
    marginTop: theme.spacing.xs,
  },
  lineTotal: {
    minWidth: 70,
    textAlign: 'right',
  },
  editWrap: {
    padding: theme.spacing.md,
  },
  formRow: {
    flexDirection: 'row',
    gap: theme.spacing.md,
  },
  formCol: {
    flex: 1,
  },
  formActions: {
    gap: theme.spacing.sm,
  },
  addActions: {
    gap: theme.spacing.sm,
  },
  actionsSection: {
    gap: theme.spacing.sm,
  },
  centeredText: {
    textAlign: 'center',
  },
});
